import { useEffect, useRef, useState } from "react";

interface NelyraLogoProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function NelyraLogo({ size = "md", className = "" }: NelyraLogoProps) {
  const sizeMap = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-12 w-12",
  };

  const dimensions = { sm: 24, md: 32, lg: 48 };
  const px = dimensions[size];
  const [src, setSrc] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Defer loading the heavy 967KB image until the element is near viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          import("@/assets/nelyra-logo.webp").then((m) => setSrc(m.default));
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ width: px, height: px, contain: "strict" }}
      className={`${sizeMap[size]} shrink-0 ${className}`}
    >
      {src && (
        <img
          src={src}
          alt="Nelyra Logo"
          width={px}
          height={px}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain"
        />
      )}
    </div>
  );
}
